import { useCallback, useRef, useState } from 'react';
import { PLAYBACK_STATUS } from '../config/videoPlayer.config';

/**
 * Keeps track of the current playback request and player status.
 *
 * A new request is created whenever the player starts a new playback action. Timers,
 * event handlers, and play promises use the request id to ignore old work after a newer
 * action has started. The hook stores status in a ref for immediate reads and in state
 * so the UI can re-render.
 */
const useVideoPlaybackRequest = () => {
  const playbackRequestIdRef = useRef(0);
  const playbackStatusRef = useRef(PLAYBACK_STATUS.idle); // Stores the latest playback status, even if the component has re-rendered
  const [playbackStatusState, setPlaybackStatusState] = useState(PLAYBACK_STATUS.idle); // Stores the playback status for the current render

  // Updates both the ref (latest playback status) and the state (playback status for the current render)
  const setPlaybackStatus = useCallback((nextStatus) => {
    playbackStatusRef.current = nextStatus;
    setPlaybackStatusState(nextStatus);
  }, []);

  const startNewPlaybackRequest = useCallback(
    (nextStatus) => {
      playbackRequestIdRef.current += 1;
      setPlaybackStatus(nextStatus);
      return playbackRequestIdRef.current;
    },
    [setPlaybackStatus],
  );

  const isStillCurrentPlaybackRequest = useCallback((requestId) => {
    return playbackRequestIdRef.current === requestId;
  }, []);

  const playbackViewState = {
    hasActiveStream:
      playbackStatusState === PLAYBACK_STATUS.playing ||
      playbackStatusState === PLAYBACK_STATUS.paused ||
      playbackStatusState === PLAYBACK_STATUS.recovering,
    paused: playbackStatusState === PLAYBACK_STATUS.paused,
    loading:
      playbackStatusState === PLAYBACK_STATUS.starting ||
      playbackStatusState === PLAYBACK_STATUS.recovering,
  };

  return {
    playbackStatusState,
    playbackStatusRef,
    playbackViewState,
    setPlaybackStatus,
    startNewPlaybackRequest,
    isStillCurrentPlaybackRequest,
  };
};

export default useVideoPlaybackRequest;
